import { createClient } from "@connectrpc/connect";
import { ISaveService } from "../../../abstractions/services/actions/ISaveService";
import { grpcTransportProvider } from "../../../comun/services/grpc/GrpcTransport";
import { mapConnectError } from "../../../comun/services/grpc/mapConnectError";
import { AplicacionService } from "../../../generated/grpc/aplicaciones_pb";
import { grpcAplicacionDatabase } from "../database/GrpcAplicacionDatabase";
import { AplicacionRequest } from "../request/AplicacionRequest";
import { AplicacionResponse } from "../response/AplicacionResponse";
import { fx_createAplicacionGrpcService, toAplicacionResponse } from "./AplicacionGrpcService";

/** Variante gRPC-Web de `AplicacionSaveService` (ver ese archivo): mismo
 *  contrato `ISaveService`, mismo guardado crear-o-actualizar (`id` ausente =
 *  crear). Tras guardar, `putOneAsync` deja la aplicación en
 *  `grpcAplicacionDatabase` -así `AplicacionGrpcService.getListAsync()` ya la
 *  ve sin esperar al próximo delta- e invalida su propio cooldown por si el
 *  guardado creó la primera aplicación de la finca (sin eso, `getListAsync()`
 *  respetaría el cooldown y no notaría que ahora hay una nueva). */
export class AplicacionSaveGrpcService
  implements ISaveService<AplicacionRequest, AplicacionResponse>
{
  async saveAsync(request: AplicacionRequest): Promise<AplicacionResponse> {
    const client = createClient(AplicacionService, grpcTransportProvider.get());

    let saved;
    try {
      saved = await client.guardar({
        id: request.id ?? undefined,
        fincaId: request.fincaId,
        cultivoId: request.cultivoId,
        cultivoTratamientoId: request.cultivoTratamientoId,
        fechaAplicacion: request.fechaAplicacion,
        vueloUrl: request.vueloUrl ?? undefined,
        detalles: request.detalles.map((detalle) => ({
          productoId: detalle.productoId,
          dosis: detalle.dosis,
        })),
        metodo: request.metodo,
        // `?? undefined` y no `?? 0` ni `?? ""`: los campos son `optional` en el
        // proto, y mandar un cero haría que el server los viera presentes en vez
        // de ausentes — "0 litros" en lugar de "no aplica".
        recipienteId: request.recipienteId ?? undefined,
        mezclaPorPlantaMl: request.mezclaPorPlantaMl ?? undefined,
        capacidadBombaLitros: request.capacidadBombaLitros ?? undefined,
        equipo: request.equipo ?? undefined,
        responsable: request.responsable ?? undefined,
        // `loteNombre` no va: es solo para la UI, el server lo resuelve del lote.
        lotes: request.lotes.map((lote) => ({
          loteId: lote.loteId,
          cantidad: lote.cantidad ?? undefined,
          plantas: lote.plantas ?? undefined,
        })),
      });
    } catch (error) {
      throw mapConnectError(error);
    }

    await grpcAplicacionDatabase.putOneAsync(saved);
    fx_createAplicacionGrpcService(request.fincaId).clearCache();
    return toAplicacionResponse(saved);
  }
}
